import React, { useEffect, useMemo, useState } from 'react';
import MqttAdminControls from '../../components/admin/agentManagement/MqttAdminControls';
import AgentResultsPanel from '../../components/admin/agentDashboard/AgentResultsPanel';
import { toast } from 'react-toastify';
import {
  fetchAgentsWithPagination,
  fetchAgentById,
} from '../../services/agent/agentServiceBackend';
import { useAgentAuthorization } from '../../hooks/useAgentAuthorization';
import MainLayout from '../../components/layout/MainLayout';
import EventSystemLayout from '@libs/components/EventSystemLayout';

/**
 * Algo Devices Admin Page
 *
 * Provides a tenant-aware interface to select an agent (Algo Device),
 * control it via MQTT admin controls, and view real-time results.
 * Restricted to admin and tenant_admin via routing protection.
 */
const AlgoDevicesPage = () => {
  const { userRole, userTenant, getAccessibleAgents } = useAgentAuthorization();

  const [loading, setLoading] = useState(false);
  const [agents, setAgents] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedAgentId, setSelectedAgentId] = useState('');
  const [selectedAgent, setSelectedAgent] = useState(null);

  const requiresTenant = userRole === 'tenant_admin' && !userTenant;

  // Load initial list of agents (tenant-aware)
  useEffect(() => {
    const loadAgents = async () => {
      setLoading(true);
      try {
        const params = {
          page: 1,
          limit: 50,
        };
        // Enforce tenant filter for tenant_admins
        if (userRole === 'tenant_admin' && userTenant) {
          params.tenant_id = userTenant;
        }
        if (search) params.search = search;

        const res = await fetchAgentsWithPagination(params);
        const list = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];

        // Filter to what the current user can access
        const accessible = getAccessibleAgents(list);
        setAgents(accessible);

        // Preselect first agent if none chosen
        if (!selectedAgentId && accessible.length) {
          setSelectedAgentId(accessible[0].agentId);
          setSelectedAgent(accessible[0]);
        }
      } catch (err) {
        console.error('Failed to load agents:', err);
        toast.error(`Failed to load agents: ${err.message || 'Unknown error'}`);
      } finally {
        setLoading(false);
      }
    };

    if (!requiresTenant) {
      loadAgents();
    }
  }, [userRole, userTenant, search]);

  // When selectedAgentId changes, fetch the full agent details from backend/cache
  useEffect(() => {
    const loadSelected = async () => {
      if (!selectedAgentId) {
        setSelectedAgent(null);
        return;
      }
      try {
        const agent = await fetchAgentById(selectedAgentId);
        setSelectedAgent(agent || null);
      } catch (err) {
        console.error('Failed to load agent:', err);
        toast.error(`Failed to load agent ${selectedAgentId}: ${err.message || 'Unknown error'}`);
      }
    };
    loadSelected();
  }, [selectedAgentId]);

  // Local filtering
  const filteredAgents = useMemo(() => {
    if (!search) return agents;
    const q = search.toLowerCase();
    return agents.filter(
      a => a?.agentName?.toLowerCase().includes(q) || a?.agentId?.toLowerCase().includes(q)
    );
  }, [agents, search]);

  const handleRefreshAgent = async () => {
    if (!selectedAgentId) return;
    try {
      const fresh = await fetchAgentById(selectedAgentId);
      setSelectedAgent(fresh || selectedAgent);
      toast.success('Agent data refreshed');
    } catch (err) {
      console.error('Refresh failed:', err);
      toast.error(`Refresh failed: ${err.message || 'Unknown error'}`);
    }
  };

  if (requiresTenant) {
    return (
      <MainLayout>
        <EventSystemLayout pageTitle="Algo Devices" />
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <EventSystemLayout pageTitle="Algo Devices">
        <div className="px-4 py-4 sm:px-0">
          {/* Agent selector */}
          <div className="bg-white shadow rounded-lg p-4 mb-6">
            <div className="flex flex-col md:flex-row md:items-end md:space-x-4 space-y-3 md:space-y-0">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
                <input
                  type="text"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Search by name or ID"
                  className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">Agent</label>
                <select
                  value={selectedAgentId}
                  onChange={e => setSelectedAgentId(e.target.value)}
                  className="w-full border border-gray-300 rounded px-3 py-2 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option value="" disabled>
                    {loading ? 'Loading agents...' : 'Select an agent'}
                  </option>
                  {filteredAgents.map(a => (
                    <option key={a.agentId} value={a.agentId}>
                      {a.agentName || a.agentId} {a.agentTenant ? `- ${a.agentTenant}` : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex-none">
                <button
                  onClick={handleRefreshAgent}
                  disabled={!selectedAgentId}
                  className="px-4 py-2 bg-gray-700 text-white rounded hover:bg-gray-800 disabled:bg-gray-400"
                >
                  Refresh
                </button>
              </div>
            </div>
          </div>

          {/* Content panels */}
          {selectedAgent ? (
            <>
              <MqttAdminControls agent={selectedAgent} onRefreshAgent={handleRefreshAgent} />
              <AgentResultsPanel agentId={selectedAgent.agentId} />
            </>
          ) : (
            <div className="bg-white shadow rounded-lg p-6 text-gray-600">No agent selected.</div>
          )}
        </div>
      </EventSystemLayout>
    </MainLayout>
  );
}

export default AlgoDevicesPage;
